import React from 'react';
import { flushSync } from 'react-dom';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Icon, Skeleton, Text, TextArea } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { NAV_DESKTOP } from '@/nova/theme/tokens';
import { useAppDispatch, useAppSelector } from '@/store';
import { kitLayersCostFor } from '@/store/slices/aiCredits';
import { cancelJob, enqueueGeneration, selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { MASCOT_AUTO, type MascotPick } from '../../../art-style/art-style-api';
import { IngredientSection } from '../../../art-style/components/IngredientSection';
import { copyReadyIn, localizedSubject, type LocalizedCopy } from '../../ad-language';
import { kitCopyFromSubject, promotionsForTracking } from '../../ad-subjects';
import { isLocked, versionKey } from '../../ad-versions';
import {
  bannerKitCostFor,
  enabledKitSizes,
  exportBannerKit,
  fetchBannerKitCatalog,
  heroAffectingChanged,
  heroDrawnWithOther,
  kitForkEntry,
  planKitShapes,
  shapeIdBySize,
  upsertBannerKit,
  versionHasUsableHero,
  type BannerKit,
  type BannerKitCatalog,
  type BannerKitParams,
  type BannerKitUpsert,
  type BannerKitVersion,
  type BannerKitVersionEntry,
  type KitCopy,
  type KitId,
  type KitRender,
} from '../../banner-kit-api';
import type { CreativeItem } from '../../creative-api';
import { assetFilename, downloadAsset } from '../../download-asset';
import { useAdVersions, type KeyedVersion, type MintedVersion } from '../../hooks/useAdVersions';
import { useLocalizedCopy } from '../../hooks/useLocalizedCopy';
import { useSiteLanguages } from '../../hooks/useSiteLanguages';
import { isCancelled } from '../../storyboard';
import { STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import {
  AdArtStyleSection,
  AdCharacterSection,
  AdDrawnWith,
  adStyleChoiceOf,
  useAdStyleRun,
  type AdStyleChoice,
} from '../AdArtStyle';
import { AdLanguageSection } from '../AdLanguageSection';
import { GenerateButton } from '../GenerateButton';
import { AssetStatsSheet, StatsPill } from '../AssetStats';
import { ComposerRailTabs, useRailEnter, type ComposerRailTab } from '../ComposerRailTabs';
import { KitLayerEditor } from '../KitLayerEditor';
import { LumioMark } from '../LumioMark';
import { TrackingLink, type TrackingLinkState } from '../TrackingLink';
import { VersionPicker, type VersionPickerItem } from '../VersionPicker';
import { KitPicker, sizeLabel } from './KitPicker';
import { SizeGrid, type SizeGridTile } from './SizeGrid';
import * as S from './BannerKitComposer.styles';

export interface BannerKitComposerProps {
  tenantId?: number;
  /** The kit being edited (the library's live entry: its versions fill in as a run lands). */
  kit: BannerKit;
  brandName: string;
  brandDomain?: string;
  /** The brand's logo variants by slot (see AdBrand.logos): written into the document at generate time, so every
   *  run places the brand's current logos. */
  brandLogos?: Record<string, string>;
  /** The brand's promotion pool (the tracking link resolves a promotion's on-site landing path from it). */
  items: CreativeItem[];
  /** Optimistically merge fields into the library's cached kit (a card and a reopen read them at once). */
  patch: (id: string, fields: Partial<BannerKit>) => void;
  onClose: () => void;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
}

/** The rail's ingredient sections. */
type RailSection = 'subject' | 'language' | 'sizes' | 'art-style' | 'character' | 'direction';

/** How long an edit rests before it is saved (one write per burst of typing). Not an animation timing. */
const SAVE_DEBOUNCE_MS = 700;

/** The edits waiting for the debounced save: the document (with the draft it was made on, `at`) and the name. */
type PendingKitSave = { params?: BannerKitParams; name?: string; at?: string };

const isActiveJob = (job: CreativeJob | undefined): boolean => job?.status === 'queued' || job?.status === 'running';

/** The run to reflect: the one this session started, else the kit's linked job, else a kit run still in flight that
 *  the link missed (the backend tags a kit run with the kit id as its root). Pure. */
export const kitRunJob = (
  kitId: string,
  jobId: string | undefined,
  jobsById: Record<string, CreativeJob>,
): CreativeJob | undefined => {
  const linked = jobId ? jobsById[jobId] : undefined;
  if (linked) return linked;
  return Object.values(jobsById).find((j) => j.kind === 'banner_kit' && j.root_banner_id === kitId && isActiveJob(j));
};

/** An owner-readable reason a run could not start. The backend's refusal reaches here as the thunk's message (its
 *  JSON body), so the known refusals are matched on their text and everything else reads as a plain retry. Pure. */
export const kitStartErrorText = (err: unknown, t: TFunction): string => {
  const raw =
    typeof err === 'object' && err !== null && 'error' in err ? String((err as { error: unknown }).error) : '';
  const text = raw.toLowerCase();
  if (text.includes('at least one size')) return t('Turn on at least one size to generate.');
  if (text.includes('not priced')) return t('Image banners are not available in this environment yet.');
  if (text.includes('already rendered')) return t('That version is already generated. Edit it to start a new version.');
  return t("That couldn't be started. Please try again.");
};

/** An owner-readable reason a layer separation could not start (the refusal's text, as for a run). Pure. */
export const layerStartErrorText = (err: unknown, t: TFunction): string => {
  const raw =
    typeof err === 'object' && err !== null && 'error' in err ? String((err as { error: unknown }).error) : '';
  if (/not available|not priced/i.test(raw)) return t('Layer editing is not available in this environment yet.');
  return t("That couldn't be started. Please try again.");
};

/** Why a layer separation failed, for its tile: the backend's "not available yet" (the upstream cannot separate
 *  layers yet), else a plain refunded failure. Pure. */
export const layerJobErrorText = (error: string | undefined, t: TFunction): string =>
  /not available/i.test(error ?? '')
    ? t('Layer editing is not available yet.')
    : t("This size couldn't be separated into layers. Your credits were refunded.");

/** The key a layer separation of this session is tracked under: the version it fills and the render's shape. */
const layerRunKey = (versionKeyText: string, shape: string): string => `${versionKeyText}|${shape}`;

/** A version as stored, without the view's `key` (what an editor forks from). Pure. */
const storedVersion = (v: KeyedVersion<BannerKitVersion>): BannerKitVersion => {
  const { key: _key, ...rest } = v;
  return rest;
};

const cloneParams = (p: BannerKitParams): BannerKitParams => JSON.parse(JSON.stringify(p)) as BannerKitParams;

/** Whether a version holds generated art (a usable hero, or any rendered size): an automatic fill never writes into
 *  one. Pure. */
const holdsArt = (v: BannerKitVersion): boolean =>
  versionHasUsableHero(v) || Object.values(v.shapes).some((r) => Boolean(r.url));

/** The subject's copy in a kit document's language: the document's own when it is this subject's in that language,
 *  else `read` when it is (an automatic fill never reaches a generated version or a draft holding art, so a Generate
 *  there carries the copy read meanwhile). undefined when neither is, or the kit has no language. Pure. */
export const kitCopyIn = (p: BannerKitParams, read: LocalizedCopy | null): LocalizedCopy | undefined => {
  const language = (p.language ?? '').trim();
  if (!language) return undefined;
  if (copyReadyIn(p.localized_copy, language, p.subject.id)) return p.localized_copy;
  return read && copyReadyIn(read, language, p.subject.id) ? read : undefined;
};

/** The copy a kit paints: its subject's own words in the kit's language (the subject type's CTA in that language when
 *  it has none). A kit with no language paints the subject's words as before. Pure. */
export const kitCopyOf = (p: BannerKitParams, read: LocalizedCopy | null): KitCopy =>
  kitCopyFromSubject(p.subject_type, localizedSubject(p.subject, kitCopyIn(p, read), p.language, p.subject_type));

/** The stored render of a size: under the shape the VERSION's own document grouped it into, else under the shape the
 *  current document does (a size turned on after the version was made); a render with an image wins. Pure. */
const renderFor = (
  version: BannerKitVersion | undefined,
  storedShape: string | undefined,
  workShape: string,
): { render?: KitRender; key?: string } => {
  if (!version) return {};
  const a = storedShape ? version.shapes[storedShape] : undefined;
  const b = workShape ? version.shapes[workShape] : undefined;
  if (a?.url) return { render: a, key: storedShape };
  if (b?.url) return { render: b, key: workShape };
  if (a) return { render: a, key: storedShape };
  return b ? { render: b, key: workShape } : {};
};

/**
 * The banner-kit composer (a wide sheet, like the video easy editor). Header: the kit's name and its version
 * picker. Left: the copy (with a Lumio draft, confirmed before the first render: these exact words are painted
 * into every size), the kits and their sizes, the art style and the art direction (the brand colours and the legal
 * line are not editable here: a new kit takes the website theme and the preset line). Right: the live size grid,
 * one tile per enabled size, filling in shape by shape while a run lands. Footer: Generate (with its credit cost),
 * Stop while running, Download ZIP once the version is generated.
 *
 * Versions follow the video rule: a run LOCKS its version, and any later edit forks a new unlocked one (selected at
 * once). A fork keeps the renders while the edit does not reach the art (kits, sizes, legal line); an edit that
 * does (copy, art direction) starts a version with no renders, so the next run paints a new hero rather than
 * reshaping the stale one. The run renders the kit's saved document, so a Generate writes the document first.
 */
export const BannerKitComposer = ({
  tenantId,
  kit,
  brandName,
  brandDomain,
  brandLogos,
  items,
  patch,
  onClose,
  onBuyCredits,
  onNotify,
}: BannerKitComposerProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const gate = useFeatureGate();
  // The layer editor is desktop only (a drag-and-resize canvas).
  const isDesktop = useMediaQuery(NAV_DESKTOP);
  const pricing = useAppSelector((state) => state.aiCredits?.pricing);
  const jobsById = useAppSelector(selectCreativeJobsMap);

  /* ---------- catalogue (static per deploy, cached by the client) ---------- */

  const [catalog, setCatalog] = React.useState<BannerKitCatalog | null>(null);
  const [catalogFailed, setCatalogFailed] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    void fetchBannerKitCatalog().then((c) => {
      if (!live) return;
      if (c) setCatalog(c);
      else setCatalogFailed(true);
    });
    return () => {
      live = false;
    };
  }, []);

  /* ---------- saving: one ordered write chain, edits debounced ---------- */

  const writeChainRef = React.useRef<Promise<unknown>>(Promise.resolve());
  const pendingRef = React.useRef<PendingKitSave>({});
  const saveTimerRef = React.useRef<number | null>(null);
  const notifyRef = React.useRef(onNotify);
  notifyRef.current = onNotify;

  // Whether a version is saved (set once the versions hook is up, below): a write naming a draft waits for it.
  const persistedOfRef = React.useRef<(key: string) => Promise<boolean>>(() => Promise.resolve(true));

  /** Queue one upsert after every earlier one (a slow write never lands over a newer one). A write naming a draft
   *  (ad_version_at, no new version) waits for that draft's own save: one the backend does not have (yet, or ever)
   *  would take nothing, so the document then goes to the kit alone. */
  // The brand this entity belongs to, as the composer opened it: a brand switch re-renders the composer once with
  // the new brand before it is closed, and its last save must still go to the entity's own brand.
  const entityTenantRef = React.useRef(tenantId);
  if (entityTenantRef.current === undefined) entityTenantRef.current = tenantId;
  const write = React.useCallback(
    (body: Omit<BannerKitUpsert, 'id'>): Promise<boolean> => {
      const tenant = entityTenantRef.current;
      if (!tenant) return Promise.resolve(false);
      const run = writeChainRef.current
        .then(async () => {
          let send = body;
          const draftKey = send.ad_version_at && !send.ad_version ? versionKey(send.ad_version_at) : undefined;
          if (draftKey && !(await persistedOfRef.current(draftKey))) {
            const { ad_version_at: _drop, ...rest } = send;
            send = rest;
          }
          await upsertBannerKit(tenant, { id: kit.id, ...send });
        })
        .then(
          () => true,
          () => false,
        );
      writeChainRef.current = run;
      return run;
    },
    [kit.id],
  );

  /** Take the debounced edits out of the queue (a caller writes them itself, or drops them). */
  const takePending = React.useCallback((): PendingKitSave => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = {};
    return pending;
  }, []);

  const flush = React.useCallback((): void => {
    const { params: p, name: n, at } = takePending();
    const body: Omit<BannerKitUpsert, 'id'> = {
      // A draft's edits are saved on the draft too (it owns its document).
      ...(p ? { ad_params: p, ...(at ? { ad_version_at: at } : {}) } : {}),
      ...(n && n.trim() ? { name: n.trim() } : {}),
    };
    if (!body.ad_params && !body.name) return;
    void write(body).then((ok) => {
      if (!ok) notifyRef.current(t("Your changes couldn't be saved. Please try again."));
    });
  }, [takePending, write, t]);

  const schedule = React.useCallback(
    (fields: PendingKitSave): void => {
      // An edit on another draft than the one waiting: the waiting edits are written to their own draft first.
      const waiting = pendingRef.current;
      if (fields.params !== undefined && waiting.params !== undefined && waiting.at !== fields.at) flush();
      pendingRef.current = { ...pendingRef.current, ...fields };
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  // Closing the composer (or a brand switch unmounting it) never drops an edit still resting in the debounce.
  const flushRef = React.useRef(flush);
  flushRef.current = flush;
  React.useEffect(() => () => flushRef.current(), []);

  /* ---------- the document and its versions ---------- */

  const [name, setName] = React.useState(kit.name);
  const nameRef = React.useRef<HTMLInputElement>(null);
  // The banner's tracking-link performance (the header's Stats pill).
  // The stats sheet is the address's (`?stats=<id>`): Back from one of its deep links reopens it.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  const statsOpen = statsSheet.value === kit.id;
  // The editor opens on the newest version, on its own document (a draft owns its document; the kit's working
  // document is only what a run reads).
  const [params, setParams] = React.useState<BannerKitParams>(() => kit.ad_versions[0]?.params ?? kit.ad_params);
  const paramsRef = React.useRef(params);

  const { versions, selected, select, current, fork, forkIfLocked, mintTarget, patchVersion, persistedOf } =
    useAdVersions<BannerKitVersion>({
      versions: kit.ad_versions,
      entityId: kit.id,
      tenantId,
      persistFork: (entry, at) =>
        tenantId
          ? upsertBannerKit(tenantId, { id: kit.id, ad_version: entry, ad_version_at: at })
          : Promise.resolve(''),
      patchEntity: (next) => patch(kit.id, { ad_versions: next }),
      onForkLost: (restored) => {
        notifyRef.current(t("Your changes couldn't be saved. Please try again."));
        // The editor shows the restored version's document again (the lost fork's edits are gone with it).
        if (restored) {
          paramsRef.current = restored.params;
          setParams(restored.params);
          // The lost fork's edits waiting in the debounce go with it (a name keeps waiting).
          const { name: waitingName } = takePending();
          if (waitingName !== undefined) schedule({ name: waitingName });
        }
      },
    });
  persistedOfRef.current = persistedOf;

  /* ---------- the run ---------- */

  const [sessionJobId, setSessionJobId] = React.useState<string | undefined>(undefined);
  const job = kitRunJob(kit.id, sessionJobId ?? kit.job_id, jobsById);
  const active = isActiveJob(job);
  const [runTargetKey, setRunTargetKey] = React.useState<string | undefined>(undefined);
  // The shapes this session's run renders (a redraw names one); null = the whole plan.
  const [runShapes, setRunShapes] = React.useState<string[] | null>(null);
  // The version the run in flight fills: the one this session targeted, else (a reopened run) the newest unlocked.
  const runKey = active
    ? runTargetKey && versions.some((v) => v.key === runTargetKey && !isLocked(v))
      ? runTargetKey
      : versions.find((v) => !isLocked(v))?.key
    : undefined;

  const [submitting, setSubmitting] = React.useState(false);
  const [stopping, setStopping] = React.useState(false);
  // No edits while a run is starting or in flight: the run renders the document saved at its start.
  const formLocked = active || submitting;

  /* ---------- edits ---------- */

  // Which art style the next run draws in (the brand's primary unless the owner picks another) and which of that
  // style's characters ('' auto, 'none' or a mascot id). Not saved in the kit's document: both start from how the
  // newest version was drawn, and ride each Generate as `style_id` and `mascot_id`.
  const styleRun = useAdStyleRun(tenantId, kit.ad_versions[0]?.art_style);
  const mascotPick = styleRun.mascotPick;
  const runStyleId = styleRun.styleId;
  // Only a read style can say a version was drawn in another one (while it is read, an edit keeps the renders, as
  // before).
  const styleKnown = styleRun.ready;

  const patchParams = React.useCallback(
    (next: BannerKitParams): void => {
      if (formLocked) return;
      const sel = current();
      if (sel && isLocked(sel)) {
        // A character or a style picked since this version was drawn: its hero holds the old one, so the fork carries
        // no renders and the next run draws the pick.
        // Like the style, the character pick counts only once the styles are known: while they load (or after both
        // reads failed) the pick reads Auto, which is not a change of the drawn one.
        const characterChanged = styleKnown && (sel.art_style?.mascot_pick ?? MASCOT_AUTO) !== mascotPick;
        const styleChanged = styleKnown && (sel.art_style?.style_id ?? '') !== runStyleId;
        forkIfLocked(
          characterChanged || styleChanged ? { params: cloneParams(next), shapes: {} } : kitForkEntry(sel, next),
        );
      } else if (sel && versionHasUsableHero(sel) && heroAffectingChanged(sel.params, next)) {
        // An unlocked version whose renders came from other copy / art direction: its hero cannot be cleared, so the
        // edit moves to a new version with no renders.
        fork({ params: cloneParams(next), shapes: {} });
      }
      // The edit lands on the draft it was made on (an art-affecting edit never reaches a draft whose art it would
      // contradict: it forked above), and is saved there with the kit's working document.
      const draft = current();
      const onDraft = draft && !isLocked(draft) ? draft : undefined;
      if (onDraft) patchVersion(onDraft.key, next);
      paramsRef.current = next;
      setParams(next);
      patch(kit.id, { ad_params: next });
      schedule({ params: next, at: onDraft?.created_at });
    },
    [
      formLocked,
      current,
      mascotPick,
      styleKnown,
      runStyleId,
      forkIfLocked,
      fork,
      patchVersion,
      patch,
      kit.id,
      schedule,
    ],
  );

  /* ---------- the language (an ingredient) and the subject's copy in it ---------- */

  const site = useSiteLanguages(tenantId);
  const localized = useLocalizedCopy({
    tenantId,
    language: params.language,
    subjectType: params.subject_type,
    subject: params.subject,
    current: params.localized_copy,
  });

  /** An automatic fill of a value the owner did not pick (the website's default language on an older kit, the
   *  subject's copy in the kit's language): written onto the open draft and the kit like an edit, but never through
   *  patchParams, so it never forks. Skipped while a run starts or is in flight, and on a generated version or a
   *  draft holding art (those keep their document as it is). */
  const autoFill = React.useCallback(
    (fields: Partial<BannerKitParams>): void => {
      if (formLocked) return;
      const sel = current();
      if (sel && (isLocked(sel) || holdsArt(sel))) return;
      const next: BannerKitParams = { ...paramsRef.current, ...fields };
      if (sel) patchVersion(sel.key, next);
      paramsRef.current = next;
      setParams(next);
      patch(kit.id, { ad_params: next });
      schedule({ params: next, at: sel?.created_at });
    },
    [formLocked, current, patchVersion, patch, kit.id, schedule],
  );
  const fillable = !formLocked && !(selected && (isLocked(selected) || holdsArt(selected)));
  const language = (params.language ?? '').trim();

  // Syncs the document with the website's languages: a kit with no language (an older one, or one created before
  // the languages were read) starts in the website's default while it holds no art.
  React.useEffect(() => {
    if (!fillable || language || site.status !== 'ready' || !site.known || !site.defaultLanguage) return;
    autoFill({ language: site.defaultLanguage });
  }, [fillable, language, site, autoFill]);

  // Syncs the document with the localization read: the subject's copy in the kit's language is kept on the document
  // (the next Generate paints it).
  React.useEffect(() => {
    if (!fillable || localized.status !== 'ready' || !localized.copy) return;
    if (!copyReadyIn(localized.copy, language, params.subject.id)) return; // an answer for another pick
    if (copyReadyIn(params.localized_copy, language, params.subject.id)) return;
    autoFill({ localized_copy: localized.copy });
  }, [fillable, localized.status, localized.copy, params.localized_copy, language, params.subject.id, autoFill]);

  /** The owner picked a language: a normal edit (a generated version, or a draft holding art, forks with no renders:
   *  every word is painted in it). */
  const onLanguage = (next: string): void => {
    if (next === (paramsRef.current.language ?? '')) return;
    patchParams({ ...paramsRef.current, language: next });
  };

  /* ---------- the art style's character (a run option, never part of the document) ---------- */

  // The picks are run options: a hero drawn in another style or with another character is never reused by a
  // Generate (heroDrawnWithOther starts it on a new version, and an edit on a generated version forks it with no
  // renders), so picking forks nothing.
  const onMascotPick = (next: MascotPick): void => {
    if (formLocked || next === mascotPick) return;
    styleRun.pickMascot(next);
  };
  const onStylePick = (next: string): void => {
    if (formLocked || next === runStyleId) return;
    styleRun.pickStyle(next);
  };
  /** The style a run draws in: a redraw that reuses its version's hero (`keepsHero`) keeps the style that hero was
   *  drawn in while the brand still lists it (its shapes recompose that hero in it), else the pick. */
  const runStyleFor = (version: BannerKitVersion | undefined, keepsHero: boolean): string => {
    const drawn = keepsHero ? version?.art_style?.style_id : undefined;
    return drawn && styleRun.styleOf(drawn) ? drawn : runStyleId;
  };
  /** Whether a run in `styleId` renders as mature (its model and price follow it). */
  const matureFor = (styleId: string): boolean =>
    styleId === runStyleId ? styleRun.mature : styleRun.styleOf(styleId)?.mature === true;

  const onKits = (next: { kits: KitId[]; sizes_off: string[] }): void =>
    patchParams({ ...paramsRef.current, kits: next.kits, sizes_off: next.sizes_off });

  const onName = (value: string): void => {
    setName(value);
    if (!value.trim()) return; // a blank name is "not provided": keep the saved one
    patch(kit.id, { name: value.trim() });
    schedule({ name: value });
  };

  // The style and character each version was left on this session (what its next run draws), so going back restores
  // them.
  const pickByKeyRef = React.useRef<Record<string, AdStyleChoice>>({});
  const onSelectVersion = (key: string): void => {
    const v = versions.find((x) => x.key === key);
    if (!v) return;
    const left = current();
    if (left) pickByKeyRef.current[left.key] = styleRun.choice;
    flush();
    select(key);
    // Show that version's document (viewing writes nothing; an edit forks it when it is locked).
    const doc = v.params;
    paramsRef.current = doc;
    setParams(doc);
    // Its style and character: the ones it was left on, else the ones its hero was drawn with (an edit that keeps the
    // renders is then no style or character change), else the current picks (no hero yet).
    const kept = pickByKeyRef.current[key];
    if (kept !== undefined) styleRun.setChoice(kept);
    else if (versionHasUsableHero(v)) styleRun.setChoice(adStyleChoiceOf(v.art_style));
  };

  /* ---------- generate / redraw / stop ---------- */

  const startRun = async (redraw?: { shapeId: string; instruction: string }): Promise<boolean> => {
    // The run's hero reuse and price follow the picked art style: none starts before it is read.
    if (!tenantId || active || submitting || !styleRun.ready) return false;
    // Nor before the subject's copy is in the kit's language: the render paints it.
    if (!copyInLanguage) return false;
    setSubmitting(true);
    try {
      // The copy is the subject's own in the kit's language (never edited by the owner): written into the document the
      // run renders, with the localized copy it came from, and kept in the editor's copy so the next edit compares
      // against it (a stale copy would read as a copy change and fork a version with no renders).
      // The brand's current logo variants go with it: the run places the real logo in code, picking the variant that
      // fits each size (the model never draws a logo).
      const cur = paramsRef.current;
      const held = kitCopyIn(cur, localized.copy);
      const doc: BannerKitParams = {
        ...cur,
        ...(held ? { localized_copy: held } : {}),
        copy: kitCopyOf(cur, localized.copy),
        ...(brandLogos && Object.keys(brandLogos).length > 0 ? { brand: { ...cur.brand, logos: brandLogos } } : {}),
      };
      paramsRef.current = doc;
      setParams(doc);
      patch(kit.id, { ad_params: doc });
      // The run renders the kit's SAVED document: write it now (the debounce may still hold the last edit).
      const { name: pendingName } = takePending();
      // The selected draft takes the document too (it owns it), so a run that never starts leaves it current.
      const editing = current();
      const draft = editing && !isLocked(editing) ? editing : undefined;
      if (draft) patchVersion(draft.key, doc);
      const saved = await write({
        ad_params: doc,
        ...(draft ? { ad_version_at: draft.created_at } : {}),
        ...(pendingName?.trim() ? { name: pendingName.trim() } : {}),
      });
      // A fork lost while it saved moved the selection back (onForkLost told the owner): nothing runs on another
      // version than the one the owner pressed Generate on.
      if (current()?.key !== editing?.key) return false;
      if (!saved) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return false;
      }
      let target: MintedVersion | null;
      if (redraw) {
        // A redraw edits one shape of the selected version (forked first when it is generated), reusing its hero.
        const sel = current();
        target = !sel
          ? null
          : isLocked(sel)
            ? forkIfLocked(kitForkEntry(sel, doc))
            : mintTarget({ params: cloneParams(doc), shapes: {} });
      } else {
        // A draft whose hero was drawn with another character is not reused: the run starts a new version, which
        // draws its own hero.
        const sel = current();
        target = heroDrawnWithOther(sel, mascotPick, runStyleId)
          ? fork({ params: cloneParams(doc), shapes: {} })
          : mintTarget({ params: cloneParams(doc), shapes: {} });
      }
      if (!target) {
        onNotify(t("That couldn't be started. Please try again."));
        return false;
      }
      // A fork carries the hero the run reuses: it must be saved before the run reads the version (a version the
      // backend does not have yet would be charged and painted a new hero).
      // A fork the backend could not save was taken back (onForkLost told the owner).
      if (!(await target.persisted)) return false;
      // The run renders `doc` into its target (the backend writes it there too): the draft shows the document it
      // is drawn from.
      patchVersion(target.key, doc);
      const instruction = redraw?.instruction.trim();
      // A redraw that reuses the version's hero keeps the character that hero was drawn with (a newer pick is drawn
      // by the next Generate, which starts from a new hero); otherwise the run draws the pick.
      const tv = current();
      const keepsHero = Boolean(redraw && tv && versionHasUsableHero(tv));
      const runPick = keepsHero ? tv?.art_style?.mascot_pick ?? MASCOT_AUTO : mascotPick;
      const runStyle = redraw ? runStyleFor(tv, keepsHero) : runStyleId;
      const res = await dispatch(
        enqueueGeneration({
          tenantId,
          placement: 'banner_kit',
          prompt: 'banner kit',
          mediaType: 'banner_kit',
          ad: {
            kit_id: kit.id,
            target_version_at: target.at,
            ...(redraw ? { only_shapes: [redraw.shapeId] } : {}),
            ...(instruction ? { instruction } : {}),
          },
          // The art style's character (Auto sends nothing), and the style the run draws in.
          mascotId: runPick || undefined,
          styleId: runStyle || undefined,
        }),
      ).unwrap();
      if (!res.ok) {
        if (res.needed !== undefined) onBuyCredits(res.needed);
        else onNotify(t("That couldn't be started. Please try again."));
        return false;
      }
      setRunTargetKey(target.key);
      setRunShapes(redraw ? [redraw.shapeId] : null);
      if (res.jobId) {
        // Link the run to the kit so a reopen (and the library card) follows its progress.
        setSessionJobId(res.jobId);
        patch(kit.id, { job_id: res.jobId });
        void write({ job_id: res.jobId });
      }
      void dispatch(fetchOwnerWallets());
      return true;
    } catch (err) {
      onNotify(kitStartErrorText(err, t));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const stop = async (): Promise<void> => {
    if (!tenantId || !job || !active || stopping) return;
    setStopping(true);
    try {
      await dispatch(cancelJob({ tenantId, jobId: job.id })).unwrap();
    } catch {
      onNotify(t("That couldn't be stopped. Please try again."));
    } finally {
      setStopping(false);
    }
  };

  /* ---------- downloads ---------- */

  const kitName = name.trim() || kit.name || t('Untitled image banner');
  const [zipBusy, setZipBusy] = React.useState(false);
  // The ZIP exported per version this session (offered again beside the tracking link).
  const [zipUrls, setZipUrls] = React.useState<Record<string, string>>({});
  const [downloadingSize, setDownloadingSize] = React.useState<string | null>(null);
  const zipName = (url: string): string => assetFilename(brandName, `${kitName} banner kit`, url, 'zip');

  const downloadZip = async (): Promise<void> => {
    const sel = selected;
    if (!tenantId || !sel || !isLocked(sel) || zipBusy) return;
    setZipBusy(true);
    onNotify(t('Preparing your image banner ZIP…'));
    try {
      const res = await exportBannerKit(tenantId, kit.id, { version_at: sel.created_at });
      if (res.ok && res.data.zip_url) {
        const url = res.data.zip_url;
        setZipUrls((cur) => ({ ...cur, [sel.key]: url }));
        await downloadAsset(url, zipName(url));
        return;
      }
      if (!res.ok && res.aborted) return;
      onNotify(
        !res.ok && res.status === 409
          ? t('Image banner downloads are not available in this environment yet.')
          : t("The image banner ZIP couldn't be prepared. Please try again."),
      );
    } finally {
      setZipBusy(false);
    }
  };

  const downloadSize = async (tile: SizeGridTile): Promise<void> => {
    const sel = selected;
    if (!tenantId || !sel || downloadingSize) return;
    setDownloadingSize(tile.size.id);
    try {
      // The file is drawn from the stored version's document (its legal line): a draft's is written first.
      if (!isLocked(sel) && !(await draftStored())) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      const res = await exportBannerKit(tenantId, kit.id, { version_at: sel.created_at, sizes: [tile.size.id] });
      const file = res.ok ? res.data.files.find((f) => f.size === tile.size.id) ?? res.data.files[0] : undefined;
      if (file?.url) {
        await downloadAsset(file.url, assetFilename(brandName, `${kitName} ${tile.size.id}`, file.url, 'jpg'));
        return;
      }
      if (!res.ok && res.aborted) return;
      onNotify(
        !res.ok && res.status === 409
          ? t('Image banner downloads are not available in this environment yet.')
          : t("That size couldn't be prepared. Please try again."),
      );
    } finally {
      setDownloadingSize(null);
    }
  };

  /* ---------- kit layers (flag- and capability-gated, desktop only) ---------- */

  // The layer separations started this session, by version and shape (a kit_layers job carries no shape on the
  // wire, and is never the kit's own `job_id`: that one drives the run and would lock the form).
  const [layerRuns, setLayerRuns] = React.useState<Record<string, string>>({});
  const [layerSubmitting, setLayerSubmitting] = React.useState(false);

  /* ---------- the rail's sections (one open at a time) ---------- */

  const [openSection, setOpenSection] = React.useState<RailSection | null>('subject');
  // On a phone the two rails are tabs (Ingredients / Banners); Generate from Ingredients shows Banners.
  const [railTab, setRailTab] = React.useState<ComposerRailTab>('ingredients');
  const railEnter = useRailEnter(railTab);

  /* ---------- derived view ---------- */

  const selectedLocked = isLocked(selected);
  const sizes = React.useMemo(() => (catalog ? enabledKitSizes(catalog, params) : []), [catalog, params]);
  const tiles: SizeGridTile[] = React.useMemo(() => {
    if (!catalog) return [];
    const work = shapeIdBySize(catalog, params);
    const stored = selected ? shapeIdBySize(catalog, selected.params) : {};
    const isRunTarget = selected !== undefined && selected.key === runKey;
    const onlyThese = isRunTarget && selected.key === runTargetKey ? runShapes : null;
    return sizes.map((size) => {
      const shapeId = work[size.id] ?? '';
      const { render, key: renderShape } = renderFor(selected, stored[size.id], shapeId);
      const pending = isRunTarget && (onlyThese ? onlyThese.includes(shapeId) : !render?.url);
      const layerJobId = selected && renderShape ? layerRuns[layerRunKey(selected.key, renderShape)] : undefined;
      const layerJob = layerJobId ? jobsById[layerJobId] : undefined;
      // A finished separation is still "landing" until the refetched version carries its layers (a second click
      // meanwhile would pay for it again).
      const layerRun = layerJob
        ? {
            pending: isActiveJob(layerJob) || (layerJob.status === 'done' && !render?.layers),
            ...(layerJob.status === 'failed' && !isCancelled(layerJob.error) && !render?.layers
              ? { error: layerJobErrorText(layerJob.error, t) }
              : {}),
          }
        : undefined;
      return { size, shapeId, render, renderShape, pending, layerRun };
    });
  }, [catalog, params, selected, sizes, runKey, runTargetKey, runShapes, layerRuns, jobsById, t]);

  const lockedCopy = kitCopyOf(params, localized.copy);
  const copyReady = lockedCopy.headline !== '' && lockedCopy.cta !== '';
  // The subject's copy in the kit's language is in hand (always for a kit with no language, unless the website's
  // default is still being read for it).
  const copyInLanguage = language
    ? kitCopyIn(params, localized.copy) !== undefined
    : !(fillable && site.status === 'loading');
  // A run into the selected version reuses its hero only while it is unlocked (a generated one gets a new version).
  const targetHasHero = Boolean(
    selected &&
      !selectedLocked &&
      versionHasUsableHero(selected) &&
      !heroDrawnWithOther(selected, mascotPick, runStyleId),
  );
  // Each render is priced at its own model: Flare draws the ones in its ratio band unless the art style is mature.
  // A render's model (Flare or Seedream) follows whether the picked style is mature: no price until that is known.
  const cost = styleRun.ready ? bannerKitCostFor(catalog, params, targetHasHero, pricing, styleRun.mature) : undefined;
  const canGenerate =
    Boolean(tenantId && catalog) &&
    copyReady &&
    copyInLanguage &&
    sizes.length > 0 &&
    !active &&
    !submitting &&
    styleRun.ready;
  const failedSizes = !active && tiles.some((tile) => !tile.render?.url && Boolean(tile.render?.error));
  const lastRunFailed = !active && job?.status === 'failed' && !selectedLocked;
  const stopped = lastRunFailed && isCancelled(job?.error);

  const pickerVersions: VersionPickerItem[] = versions.map((v) => ({
    key: v.key,
    createdAt: Date.parse(v.created_at),
    locked: isLocked(v),
    generating: v.key === runKey,
  }));

  /* ---------- kit layers: separate, edit, save ---------- */

  const layersOn = gate.isEnabled('creatives-kit-layers') && catalog?.features.layers === true && isDesktop;
  const canEditLayers = layersOn && Boolean(tenantId) && !formLocked && !layerSubmitting;
  const layerModel = selected?.params.model || params.model || catalog?.model || '';

  const [layersTile, setLayersTile] = React.useState<SizeGridTile | null>(null);
  const closeLayersSheet = React.useCallback((): void => setLayersTile(null), []);
  const { closeHandle: layersHandle, dismiss: dismissLayers } = useSheetDismiss(closeLayersSheet);
  const [layerEditor, setLayerEditor] = React.useState<{
    version: BannerKitVersion;
    shape: string;
    tile: SizeGridTile;
  } | null>(null);
  const closeLayerEditor = React.useCallback((): void => setLayerEditor(null), []);

  /** The other sizes cut from a tile's render (they change with it). */
  const siblingsOf = (tile: SizeGridTile): string[] => {
    if (!catalog || !tile.renderShape) return [];
    const plan = planKitShapes(catalog, selected?.params ?? params);
    const group =
      plan.find((s) => s.id === tile.renderShape) ??
      planKitShapes(catalog, params).find((s) => s.id === tile.renderShape);
    return (group?.sizes ?? []).filter((id) => id !== tile.size.id).map((id) => id.replace('x', '×'));
  };

  const openLayers = (tile: SizeGridTile): void => {
    if (!canEditLayers || !selected || !tile.renderShape || !tile.render?.url) return;
    if (tile.render.layers && tile.render.layers.items.length > 0) {
      setLayerEditor({ version: storedVersion(selected), shape: tile.renderShape, tile });
      return;
    }
    setLayersTile(tile);
  };

  /** Separate a tile's render into layers: a job on the selected version (forked first when it is generated, so a
   *  generated version never changes), charged `kit_layers:<model>`. */
  const startLayers = async (tile: SizeGridTile): Promise<boolean> => {
    const sel = current();
    const shape = tile.renderShape;
    if (!tenantId || !sel || !shape || active || submitting || layerSubmitting) return false;
    setLayerSubmitting(true);
    try {
      // The worker reads the render from the target version, so the fork carrying it must be saved first.
      const target = isLocked(sel) ? forkIfLocked() : mintTarget({ params: cloneParams(sel.params), shapes: {} });
      if (!target) {
        onNotify(t("That couldn't be started. Please try again."));
        return false;
      }
      // A fork the backend could not save was taken back (onForkLost told the owner).
      if (!(await target.persisted)) return false;
      const res = await dispatch(
        enqueueGeneration({
          tenantId,
          placement: 'banner_kit',
          prompt: 'kit layers',
          mediaType: 'kit_layers',
          ad: { kit_id: kit.id, target_version_at: target.at, shape },
        }),
      ).unwrap();
      if (!res.ok) {
        if (res.needed !== undefined) onBuyCredits(res.needed);
        else onNotify(t("That couldn't be started. Please try again."));
        return false;
      }
      const jobId = res.jobId;
      if (jobId) setLayerRuns((cur) => ({ ...cur, [layerRunKey(target.key, shape)]: jobId }));
      void dispatch(fetchOwnerWallets());
      return true;
    } catch (err) {
      onNotify(layerStartErrorText(err, t));
      return false;
    } finally {
      setLayerSubmitting(false);
    }
  };

  const confirmLayers = async (): Promise<void> => {
    if (!layersTile) return;
    const ok = await startLayers(layersTile);
    if (!ok) return;
    // Leave the busy state in this frame, so the sheet (which refuses to close while busy) closes now.
    flushSync(() => setLayerSubmitting(false));
    dismissLayers();
  };

  /** Whether the selected version's document is stored as shown (the layer editor saves only then: the backend
   *  builds its new version from the stored document). A draft's document is written once more and that write's
   *  result is the answer; a generated version's never changes. */
  const draftStored = async (): Promise<boolean> => {
    flush();
    const sel = current();
    if (!sel || isLocked(sel)) return true;
    const ok = await write({ ad_params: paramsRef.current, ad_version_at: sel.created_at });
    // A fork lost while it saved moved the selection back: its document is not stored anywhere.
    return ok && current()?.key === sel.key;
  };

  /** The layer editor kept edited layers: a new unlocked version, selected (the editor reads from it next). */
  const snapshotLayers = (entry: BannerKitVersionEntry): MintedVersion | null => fork(entry);

  /** The layer editor saved: show the new generated version at once (the backend wrote it) and select it. */
  const kitRef = React.useRef(kit);
  kitRef.current = kit;
  const showFlattened = (version: BannerKitVersion): void => {
    const key = versionKey(version.created_at);
    const list = kitRef.current.ad_versions;
    if (!list.some((v) => versionKey(v.created_at) === key)) patch(kit.id, { ad_versions: [version, ...list] });
    flush();
    select(key);
    paramsRef.current = version.params;
    setParams(version.params);
  };

  const layersCost = kitLayersCostFor(pricing, layerModel);
  const layerSiblings = layersTile ? siblingsOf(layersTile) : [];
  const layerRenderSize = (shape: string): { w: number; h: number } | undefined => {
    const s = catalog?.shapes.find((x) => x.id === shape);
    return s ? { w: s.render_w, h: s.render_h } : undefined;
  };

  /* ---------- render ---------- */

  const handleClose = (): void => {
    flush();
    onClose();
  };

  // The rail's status line, above Generate: the run's progress, or the one thing the owner needs to know.
  const status = active ? (
    <>
      {job?.stage === 'hero' ? (
        <S.FootText text="Painting the hero image…" />
      ) : job?.total ? (
        <S.FootText
          text="Rendering sizes · {{done}} of {{total}}"
          placeholders={{ done: job.completed ?? 0, total: job.total }}
        />
      ) : (
        <S.FootText text="Starting…" />
      )}
      <S.ProgressTrack aria-hidden="true">
        <S.ProgressFill $pct={job?.total ? ((job.completed ?? 0) / job.total) * 100 : 0} />
      </S.ProgressTrack>
    </>
  ) : stopped ? (
    <S.FootText text="Stopped. Your credits were refunded." />
  ) : catalog && sizes.length === 0 ? (
    <S.Hint text="Turn on at least one size to generate." />
  ) : !copyInLanguage ? (
    localized.status === 'error' ? (
      <S.Hint text="The text couldn't be prepared in this language. Open Language to try again." />
    ) : (
      <S.Hint text="Preparing the text in this language…" />
    )
  ) : !copyReady ? (
    <S.Hint text="This subject has no headline to put on the banners." />
  ) : selectedLocked ? (
    <S.Hint text="This version is generated. Edit anything to start a new version." />
  ) : null;

  // Nothing rendered, rendering or failed in the selected version: the guide stands in for a grid of empty boxes.
  const showGuide =
    catalog !== null && !active && !tiles.some((tile) => tile.pending || tile.render?.url || tile.render?.error);

  const toggleSection = (id: RailSection): void => setOpenSection((cur) => (cur === id ? null : id));
  const subjectTitle =
    params.subject_type === 'game'
      ? t('Game')
      : params.subject_type === 'gamification'
        ? t('Gamification')
        : t('Promotion');

  return (
    <>
      <Sheet
        onClose={handleClose}
        ariaLabel="Banner kit"
        maxWidth="1100px"
        maxHeight="100%"
        header={
          <S.Header>
            <S.HeaderLeft>
              <S.TitleWrap>
                <S.TitleInput
                  ref={nameRef}
                  value={name}
                  size={Math.max((name || t('Name this image banner')).length + 1, 4)}
                  onChange={(e) => onName(e.target.value)}
                  placeholder={t('Name this image banner')}
                  aria-label={t('Image banner name')}
                />
                <S.TitleEditIcon type="button" aria-label={t('Edit name')} onClick={() => nameRef.current?.focus()}>
                  <Icon name="edit" size={14} />
                </S.TitleEditIcon>
              </S.TitleWrap>
              <VersionPicker
                versions={pickerVersions}
                value={selected?.key}
                onChange={onSelectVersion}
                disabled={submitting}
              />
              <StatsPill onClick={() => statsSheet.open(kit.id)} />
            </S.HeaderLeft>
          </S.Header>
        }
      >
        <ComposerRailTabs value={railTab} onChange={setRailTab} outputLabel="Banners" />
        <S.Split $mobileTab={railTab} $railEnter={railEnter}>
          {/* Left: the ingredients (one open at a time, like the Art Style page); Generate pinned to the bottom. */}
          <S.KitRail>
            <S.RailScroll>
              <IngredientSection
                id="kit-subject"
                title={subjectTitle}
                icon="megaphone"
                summary={params.subject.name}
                open={openSection === 'subject'}
                onToggle={() => toggleSection('subject')}
              >
                <S.SubjectCard>
                  {params.subject.image_url ? <S.SubjectThumb src={params.subject.image_url} alt="" /> : null}
                  <S.SubjectCopy>
                    <S.CopyHeadline raw text={lockedCopy.headline || params.subject.name} />
                    {lockedCopy.subhead ? <S.CopySub raw text={lockedCopy.subhead} /> : null}
                  </S.SubjectCopy>
                </S.SubjectCard>
                <S.LockNote>
                  <Icon name="lock" size={12} />
                  {params.subject_type === 'game' ? (
                    <Text tag="span" text="The copy comes from the game, so every banner matches your site." />
                  ) : params.subject_type === 'gamification' ? (
                    <Text tag="span" text="The copy comes from the feature, so every banner matches your site." />
                  ) : (
                    <Text
                      tag="span"
                      text="The copy comes from the promotion, so every banner matches the live offer."
                    />
                  )}
                </S.LockNote>
              </IngredientSection>

              <AdLanguageSection
                id="kit-language"
                open={openSection === 'language'}
                onToggle={() => toggleSection('language')}
                value={language}
                siteLanguages={site}
                disabled={formLocked}
                onChange={onLanguage}
                copyStatus={localized.status}
                translated={(localized.copy?.translated.length ?? 0) > 0}
                onRetry={localized.retry}
              />

              <IngredientSection
                id="kit-sizes"
                title={t('Sizes')}
                icon="grid"
                summary={t('{{count}} sizes', { count: sizes.length })}
                open={openSection === 'sizes'}
                onToggle={() => toggleSection('sizes')}
              >
                {catalog ? (
                  <KitPicker
                    catalog={catalog}
                    kits={params.kits}
                    sizesOff={params.sizes_off}
                    disabled={formLocked}
                    onChange={onKits}
                  />
                ) : catalogFailed ? (
                  <S.Hint text="The size catalogue couldn't be loaded. Close the banner and open it again to retry." />
                ) : (
                  <Skeleton block height={160} radius={12} />
                )}
              </IngredientSection>

              <IngredientSection
                id="kit-art-style"
                title={t('Art style')}
                icon="palette"
                summary={styleRun.styleSummary}
                open={openSection === 'art-style'}
                onToggle={() => toggleSection('art-style')}
              >
                <S.ArtStyleBody>
                  <AdArtStyleSection
                    tenantId={tenantId}
                    run={styleRun}
                    onPickStyle={onStylePick}
                    disabled={formLocked}
                  />
                  <AdDrawnWith artStyle={selected?.art_style} />
                </S.ArtStyleBody>
              </IngredientSection>

              {styleRun.hasCast ? (
                <IngredientSection
                  id="kit-character"
                  title={t('Character')}
                  icon="user"
                  summary={styleRun.characterSummary(mascotPick)}
                  open={openSection === 'character'}
                  onToggle={() => toggleSection('character')}
                >
                  <AdCharacterSection
                    tenantId={tenantId}
                    value={mascotPick}
                    onChange={onMascotPick}
                    disabled={formLocked}
                    cast={styleRun.cast}
                  />
                </IngredientSection>
              ) : null}

              <IngredientSection
                id="kit-direction"
                title={t('Direction')}
                icon="vibe"
                optional
                summary={params.art_direction.trim() || t('Optional')}
                open={openSection === 'direction'}
                onToggle={() => toggleSection('direction')}
              >
                <TextArea
                  label={t('Style, mood or colours')}
                  value={params.art_direction}
                  onChange={(v) => patchParams({ ...paramsRef.current, art_direction: v })}
                  placeholder={t('For example: neon night, deep purple and gold.')}
                  rows={3}
                  disabled={formLocked}
                />
              </IngredientSection>
            </S.RailScroll>

            <S.RailFoot>
              {status ? <S.RailStatus>{status}</S.RailStatus> : null}
              {active ? (
                <Button
                  variant="outline"
                  icon="stop"
                  fullWidth
                  label={stopping ? t('Stopping…') : t('Stop')}
                  disabled={stopping || !tenantId}
                  onClick={() => void stop()}
                />
              ) : (
                <GenerateButton
                  label={t('Generate banners')}
                  cost={cost}
                  disabled={!canGenerate}
                  onClick={() => {
                    setRailTab('output');
                    void startRun();
                  }}
                />
              )}
            </S.RailFoot>
          </S.KitRail>

          {/* Right: the sizes as they render (or the guide before the first run); downloads pinned to the bottom. */}
          <S.KitOutput>
            <S.OutputScroll>
              {lastRunFailed && !stopped ? (
                <Callout
                  tone="danger"
                  title={t("This image banner couldn't be generated")}
                  text={job?.error || t('Your credits were refunded. Please try again.')}
                />
              ) : null}
              {failedSizes ? (
                <Callout
                  tone="warning"
                  text={t(
                    'Some sizes could not be rendered and their credits were refunded. Generate the banners again to try them.',
                  )}
                />
              ) : null}
              {showGuide ? (
                <S.Guide>
                  <S.GuideIcon>
                    {/* The Lumio brand mark for this AI feature (never an approximated sparkle/star). */}
                    <LumioMark size={24} />
                  </S.GuideIcon>
                  <S.GuideTitle text="Build your image banners" />
                  <S.GuideText text="Lumio paints one hero image with your subject's own copy, then renders every size from it." />
                  <S.GuideSteps>
                    <S.GuideStep>
                      <S.GuideStepNum>1</S.GuideStepNum>
                      <S.GuideStepText text="Pick the sizes you need" />
                    </S.GuideStep>
                    <S.GuideStep>
                      <S.GuideStepNum>2</S.GuideStepNum>
                      <S.GuideStepText text="Generate the banners" />
                    </S.GuideStep>
                    <S.GuideStep>
                      <S.GuideStepNum>3</S.GuideStepNum>
                      <S.GuideStepText text="Download the ZIP" />
                    </S.GuideStep>
                  </S.GuideSteps>
                </S.Guide>
              ) : catalog ? (
                tiles.length > 0 ? (
                  <SizeGrid
                    tiles={tiles}
                    canDownload={!active}
                    downloadingSize={downloadingSize}
                    onDownload={(tile) => void downloadSize(tile)}
                    canEditLayers={canEditLayers}
                    onEditLayers={openLayers}
                  />
                ) : (
                  <S.EmptyGrid text="Turn on a kit to see its sizes here." />
                )
              ) : catalogFailed ? null : (
                <Skeleton block height={240} radius={12} />
              )}
              {selected && selectedLocked ? (
                <TrackingLink
                  key={selected.key}
                  kind="kit"
                  creativeId={kit.id}
                  versionKey={selected.key}
                  promotions={promotionsForTracking(selected.params.subject)}
                  poolItems={items}
                  brandDomain={brandDomain}
                  adName={kitName}
                  videoUrl={zipUrls[selected.key]}
                  downloadName={zipUrls[selected.key] ? zipName(zipUrls[selected.key]) : undefined}
                  downloadLabel={t('Download ZIP')}
                  tenantId={tenantId}
                  pickDestination
                  subject={
                    selected.params.subject.id
                      ? { type: selected.params.subject_type, subject: selected.params.subject }
                      : undefined
                  }
                  onBuyCredits={onBuyCredits}
                />
              ) : null}
            </S.OutputScroll>
            {selectedLocked ? (
              <S.OutputFoot>
                <Button
                  variant="outline"
                  icon="download"
                  label={zipBusy ? t('Preparing…') : t('Download ZIP')}
                  disabled={zipBusy}
                  onClick={() => void downloadZip()}
                />
              </S.OutputFoot>
            ) : null}
          </S.KitOutput>
        </S.Split>
      </Sheet>

      {layersTile ? (
        <Sheet
          title={t('Edit layers · {{size}}', { size: sizeLabel(layersTile.size) })}
          onClose={closeLayersSheet}
          closeHandle={layersHandle}
          busy={layerSubmitting}
          footer={
            <S.FootActions>
              <Button variant="text" label={t('Cancel')} onClick={dismissLayers} disabled={layerSubmitting} />
              <GenerateButton
                compact
                label={t('Separate layers')}
                cost={layersCost}
                disabled={layerSubmitting || active || submitting || !tenantId}
                onClick={() => void confirmLayers()}
              />
            </S.FootActions>
          }
        >
          <S.RedrawBody>
            {layersTile.render?.url ? (
              <S.RedrawPreview $ratio={layersTile.size.w / layersTile.size.h}>
                <S.TileImg
                  src={layersTile.render.url}
                  alt={t('The {{size}} banner', { size: sizeLabel(layersTile.size) })}
                />
              </S.RedrawPreview>
            ) : null}
            <S.Hint text="Lumio separates this size into layers (logo, headline, sub-line, call to action, subject and decorations), so you can move, resize, hide or rewrite each one." />
            <S.Hint text="It takes about two minutes. You can keep working on the banner meanwhile." />
            {layerSiblings.length > 0 ? (
              <S.Hint
                text="Sizes of the same shape change with it: {{sizes}}."
                placeholders={{ sizes: layerSiblings.join(', ') }}
              />
            ) : null}
          </S.RedrawBody>
        </Sheet>
      ) : null}

      {layerEditor && tenantId ? (
        <KitLayerEditor
          tenantId={tenantId}
          kitId={kit.id}
          version={layerEditor.version}
          shape={layerEditor.shape}
          sizeText={sizeLabel(layerEditor.tile.size)}
          siblingSizes={siblingsOf(layerEditor.tile)}
          renderSize={layerRenderSize(layerEditor.shape)}
          model={layerEditor.version.params.model || catalog?.model || ''}
          onSnapshot={snapshotLayers}
          onFlattened={showFlattened}
          documentStored={draftStored}
          onBuyCredits={onBuyCredits}
          onNotify={onNotify}
          onClose={closeLayerEditor}
        />
      ) : null}

      {statsOpen ? (
        <AssetStatsSheet kind="kit" creativeId={kit.id} name={name} onClose={statsSheet.close} />
      ) : null}
    </>
  );
};

export default BannerKitComposer;
